import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import PageHero from "../components/PageHero";
import FinalCta from "../components/FinalCta";
import StatsCounter from "../components/StatsCounter";
import { IMG } from "../lib/content";

const segments = [
  {
    title: "Hotels & Hospitality",
    body: "Lobbies, ballrooms, ensuites — finish standards that match a five-star arrival experience.",
    image: IMG.commercialLobby,
  },
  {
    title: "Strata & Apartments",
    body: "Common areas restored with minimum disruption to residents. Out-of-hours crews available.",
    image: IMG.harborView,
  },
  {
    title: "Corporate & Retail",
    body: "Reception areas, flagship stores and façade stone refinished to brand-defining quality.",
    image: IMG.mosaicTerrazzo,
  },
];

const inclusions = [
  "Dedicated account manager",
  "Documented SWMS for every site",
  "$20M public liability cover",
  "Quarterly stone health audits",
  "Out-of-hours and overnight crews",
  "Transparent KPI reporting",
];

export default function Commercial() {
  return (
    <main data-testid="commercial-page">
      <PageHero
        eyebrow="Commercial"
        title="Programs that protect"
        italic="asset value."
        subtitle="Scheduled stone care for hotels, strata buildings, retail flagships and corporate towers across Sydney."
        image={IMG.commercialLobby}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
          {segments.map((s, i) => (
            <motion.div
              key={s.title}
              data-testid={`commercial-segment-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.07 }}
              className="bg-white border border-black/10 hover-lift hover:border-[#C9A86A] hover:shadow-[0_24px_60px_-30px_rgba(0,0,0,0.18)]"
            >
              <div className="img-zoom-wrap aspect-[5/4]">
                <img src={s.image} alt={s.title} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-8">
                <h3 className="font-display-bold text-2xl tracking-tight text-[#0A0A0A]">{s.title}</h3>
                <p className="mt-4 text-[#525252] text-[15px] leading-relaxed">{s.body}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <StatsCounter />

      <section className="py-24 md:py-32 bg-[#F7F4F0] text-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-[#B8965A]">
              <span className="block w-8 h-px bg-[#C9A86A]" />
              <span className="text-[11px] uppercase tracking-[0.3em]">Inclusions</span>
            </div>
            <h2 className="mt-6 font-display-bold text-4xl md:text-5xl tracking-tighter">
              Built for facilities <span className="font-serif-italic font-light text-[#B8965A]">teams.</span>
            </h2>
            <p className="mt-6 max-w-md text-[#525252] leading-relaxed">
              We make commercial procurement painless: clear scope, fixed pricing,
              audited safety, predictable outcomes.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
            {inclusions.map((it) => (
              <div key={it} className="flex items-start gap-3 border-b border-black/15 py-4">
                <CheckCircle2 className="w-5 h-5 text-[#B8965A] mt-0.5" strokeWidth={1.4} />
                <span className="text-[15px]">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
